import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { api, ApiError, setUnauthorizedHandler } from './api';

/**
 * Admin SPA auth store. Tighter than the candidate-app version:
 *  - persists user shape so a reload doesn't flash unauthenticated
 *  - exposes a `isAdmin` selector that gates every page on role
 *  - exposes a `mfaRequired` flag to lock the UI behind MFA if it
 *    hasn't been verified during this session (CLAUDE.md "mandatory
 *    MFA enforced at sign-in")
 */
export type AdminRole = 'admin' | 'superadmin';

/**
 * MFA verification lives in sessionStorage, not the persisted (localStorage)
 * store: it must survive a page reload but NOT outlive the browser session, so
 * closing the tab/window on a shared computer re-locks the portal. See the
 * `partialize` note below — `user` persists across sessions, this flag does not.
 */
const MFA_SESSION_KEY = 'gmc-admin-mfa-verified';

function readMfaVerified(): boolean {
  try {
    return sessionStorage.getItem(MFA_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function writeMfaVerified(verified: boolean): void {
  try {
    if (verified) {
      sessionStorage.setItem(MFA_SESSION_KEY, '1');
    } else {
      sessionStorage.removeItem(MFA_SESSION_KEY);
    }
  } catch {
    // sessionStorage unavailable (private mode / disabled) — fail closed;
    // the in-memory flag still gates this tab until reload.
  }
}

export interface AdminUser {
  id: string;
  email: string;
  display_name: string;
  locale: 'en' | 'nb' | 'sv';
  role: AdminRole | 'candidate' | 'agent' | null;
  status: 'active' | 'suspended' | 'pending_deletion' | null;
  email_verified_at: string | null;
  requires_mfa: boolean;
  /** True once the user has a confirmed authenticator; drives enroll-vs-verify. */
  mfa_enrolled: boolean;
}

interface AuthState {
  user: AdminUser | null;
  loading: boolean;
  /** True after the user successfully completes /auth/mfa/verify in this session. */
  mfaVerifiedThisSession: boolean;
  hydrate: () => Promise<void>;
  signOut: () => Promise<void>;
  setUser: (user: AdminUser | null) => void;
  setMfaVerified: (verified: boolean) => void;
}

export const useAuth = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      loading: false,
      // Rehydrate from sessionStorage so a reload keeps the tab unlocked.
      mfaVerifiedThisSession: readMfaVerified(),
      setUser: (user) => set({ user }),
      setMfaVerified: (verified) => {
        writeMfaVerified(verified);
        set({ mfaVerifiedThisSession: verified });
      },
      hydrate: async () => {
        set({ loading: true });
        try {
          const { data } = await api<{ data: AdminUser }>('/me');
          set({ user: data, loading: false });
        } catch (e) {
          if (e instanceof ApiError && e.status === 401) {
            writeMfaVerified(false);
            set({ user: null, loading: false, mfaVerifiedThisSession: false });
            return;
          }
          set({ loading: false });
        }
      },
      signOut: async () => {
        try {
          await api('/auth/logout', { method: 'POST' });
        } finally {
          writeMfaVerified(false);
          set({ user: null, mfaVerifiedThisSession: false });
        }
      },
    }),
    {
      name: 'gmc-admin-auth',
      // Only `user` is persisted here (localStorage) so it outlives the browser
      // session. MFA verification is deliberately kept OUT of this store and
      // lives in sessionStorage instead (see readMfaVerified/writeMfaVerified),
      // so a reload keeps the tab unlocked but closing the browser re-locks it.
      partialize: (state) => ({ user: state.user }),
    },
  ),
);

export function isAdmin(user: AdminUser | null): user is AdminUser & { role: AdminRole } {
  return user !== null && (user.role === 'admin' || user.role === 'superadmin');
}

// A 401 from any page's api() call means the server-side session is gone
// (expired, revoked, ...) even though a user was still persisted locally.
// Clearing it here — the one place that touches the store from outside a
// component — makes AdminRoute's existing `user === null` check redirect
// to /sign-in on the next render, instead of the page that hit the 401
// being left showing its own dead-end error message.
setUnauthorizedHandler(() => {
  writeMfaVerified(false);
  useAuth.setState({ user: null, mfaVerifiedThisSession: false });
});
